/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2023
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import XLSX, { WorkSheet } from 'xlsx';
import _ from 'lodash';
import PapaParse from 'papaparse';
import { IAny } from '../../../../DashboardKPIWidgets/IEnergyManagementProps';

const usingFileReader = async (file: File) =>
  new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      /* e.target.result is an ArrayBuffer */
      if (e.target) {
        res(e.target.result);
      }
    };
    reader.onerror = (e) => {
      rej(e.target?.error);
    };
    reader.readAsArrayBuffer(file);
  });

/**
 * Reads  excel file
 * Returns first worksheet
 * @param validFile JS File object
 * @returns Promise with WorkSheet object
 */
export const readExcelFile = async (validFile: File): Promise<WorkSheet> => {
  const excelData = await usingFileReader(validFile);
  const workbook = XLSX.read(excelData);
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  return worksheet;
};

export interface IExcelDataJSON {
  [key: string]: IAny;
}

export interface IHeaderData {
  mainHeaders: IAny[];
  subHeaderStartIndices: number[];
  subHeaders: IAny[];
}

export interface IReadOnboardingExcelData {
  headers: IHeaderData;
  jsonData: IExcelDataJSON[];
  rowData: IAny[][];
}

/**
 * Returns raw rowData, parsed headers and data for specified format
 * This is specific to the format. Make changes according to format changes
 * @remarks
 * Current worksheet format (MH: Main Header, SH: SubHeader)
 *
 * Row1 :    MH_1     |     MH_2
 *
 * Row2 : SH_1 | SH_2 | SH_3 | SH_4
 *
 * jsonData { SH_1: cell1_val, SH_2: cell2_val, SH_3: cell3_val, SH_4: cell4_val }
 *
 * @param worksheet Worksheet 1 extracted from uploaded file
 * @returns returns parsed headers, jsonArray with keys as parsed headers and raw rowData
 */
export const convertWorkSheetToOnboardingExcelData = (
  worksheet: WorkSheet
): IReadOnboardingExcelData => {
  const rowData = XLSX.utils.sheet_to_json<IAny[][]>(worksheet, {
    header: 1,
    defval: ''
  });
  const mainHeaders = rowData[0];
  const subHeaderStartIndices: number[] = [];
  _.forEach(mainHeaders, (h, i) => {
    if (!_.isEmpty(h)) {
      subHeaderStartIndices.push(i);
    }
  });
  const secRow = 1;
  const subHeaders = rowData[secRow];
  const jsonData = XLSX.utils.sheet_to_json<IAny[][]>(worksheet, {
    defval: '',
    range: secRow
  });
  const headers = { mainHeaders, subHeaderStartIndices, subHeaders };
  return { headers, jsonData, rowData };
};

export const readOnboardingDataFromExcelFile = async (
  excelFile: File
): Promise<IReadOnboardingExcelData> => {
  const worksheet = await readExcelFile(excelFile);
  const excelData = convertWorkSheetToOnboardingExcelData(worksheet);
  return excelData;
};

/**
 * Reads provided csv file
 * @param file JS File Object
 * @returns row array
 */
export const readCSVFile = async (file: File) => {
  const parserPromise = async () =>
    new Promise<IAny[]>((res, rej) => {
      PapaParse.parse(file, {
        complete: (results) => {
          if (results.errors && results.errors.length > 0) {
            rej(results.errors);
          } else {
            res(results.data);
          }
        },
        error: (err) => {
          rej(err);
        },
        skipEmptyLines: true
      });
    });
  const rowData = await parserPromise();
  return rowData;
};

/**
 * Returns parsed headers
 * This is specific to the format. Make changes according to format changes
 * @remarks
 * Current csv format (MH: Main Header, SH: SubHeader)
 *
 * Row1 :    MH_1     |     MH_2
 *
 * Row2 : SH_1 | SH_2 | SH_3 | SH_4
 *
 * @param rowData RowData returned by papaparse.parse function
 * @returns returns parsed headers
 */
const extractHeadersFromCSV = (rowData: IAny[][]) => {
  const mainHeaders = rowData[0];
  const subHeaderStartIndices: number[] = [];
  _.forEach(mainHeaders, (h, i) => {
    if (!_.isEmpty(h)) {
      subHeaderStartIndices.push(i);
    }
  });
  const subHeaders = rowData[1];
  return { mainHeaders, subHeaderStartIndices, subHeaders };
};

/**
 * Returns raw rowData, headers and parsed json data using the headers
 * This is specific to the format. Make changes according to format changes
 * @remarks
 * Current csv format: see comment of extractHeadersFromCSV function
 * jsonData { SH_1: cell1_val, SH_2: cell2_val, SH_3: cell3_val, SH_4: cell4_val }
 *
 * @param worksheet Worksheet 1 extracted from uploaded file
 * @returns returns parsed headers, jsonArray with keys as parsed headers and raw rowData
 */
const transformCSVRowDataToHeaderJSONData = (
  rowData: IAny[][]
): IReadOnboardingExcelData => {
  const headers = extractHeadersFromCSV(rowData);
  const { subHeaders } = headers;
  const jsonData: IExcelDataJSON[] = [];
  const _dataRowStartIndex = 2;
  const dataRows = _.slice(rowData, _dataRowStartIndex);

  _.forEach(dataRows, (row) => {
    const jsonRow: IExcelDataJSON = {};
    _.forEach(subHeaders, (headerVal, i) => {
      if (!_.isEmpty(headerVal) || !_.isEmpty(row[i])) {
        jsonRow[headerVal] = row[i];
      }
    });
    jsonData.push(jsonRow);
  });

  return { headers, jsonData, rowData };
};

export const readOnboardingDataFromCSVFile = async (
  csvFile: File
): Promise<IReadOnboardingExcelData> => {
  const rowData = await readCSVFile(csvFile);
  const { headers, jsonData } = transformCSVRowDataToHeaderJSONData(rowData);
  return { rowData, headers, jsonData };
};
